// #tag: PUBLIC AND STUDENT LAYOUT SHELL
// #purpose: Implements responsive web layouts, top app bars, floating mobile navigation docks, and user profile menus.
import React, { useMemo, useState } from 'react';
import { AnimatePresence as FramerAnimatePresence, motion, type AnimatePresenceProps } from 'framer-motion';
import { Bell, LayoutDashboard, LogOut, Settings, Shield, User } from 'lucide-react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { DEFAULT_LOGO_PATH } from '../../config/constants';
import { useAuth } from '../../context/AuthProvider';
import { useNavItems } from '../../hooks/useNavItems';
import { useNotifications, useSiteConfig } from '../../hooks/useExamData';
import { FloatingDock } from '../navigation/FloatingDock';
import { LoadingState } from '../states/StateViews';

const AnimatePresence = FramerAnimatePresence as React.FC<React.PropsWithChildren<AnimatePresenceProps>>;

function useActiveNavPath(items: Array<{ path: string }>): string | null {
  const { pathname } = useLocation();
  return useMemo(() => {
    const matches = items.filter((item) =>
      item.path === '/' ? pathname === '/' : pathname === item.path || pathname.startsWith(`${item.path}/`),
    );
    if (matches.length === 0) return null;
    return matches.reduce((best, item) => (item.path.length > best.path.length ? item : best)).path;
  }, [items, pathname]);
}

// #tag: USER PROFILE POPUP BUTTON
// #purpose: Displays current user avatar, badge, and quick navigation menu for settings and sign-out.
function ProfileButton({ compact = false }: { compact?: boolean }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { user, logout, isAdmin, isSuperAdmin, usesAdminConsoleOnly } = useAuth();

  const roleLabel = isSuperAdmin
    ? 'Super Admin'
    : isAdmin
      ? (user?.role?.replace('_', ' ') ?? 'Admin')
      : null;

  return (
    <div className="profile-wrap">
      <button
        type="button"
        className={`avatar-button ${compact ? 'is-compact' : ''}${isAdmin ? ' is-admin' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-label="Open profile menu"
        aria-expanded={open}
      >
        {isAdmin ? <Shield size={compact ? 18 : 19} aria-hidden="true" /> : <User size={compact ? 18 : 19} aria-hidden="true" />}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            className="profile-menu"
            initial={{ opacity: 0, y: 8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ duration: 0.16 }}
          >
            {user && (
              <div className="profile-menu-head">
                <strong>{user.name}</strong>
                <span>{user.email ?? user.phone ?? 'Signed in'}</span>
                {roleLabel && <span className="profile-menu-role">{roleLabel}</span>}
              </div>
            )}
            {isAdmin && !usesAdminConsoleOnly ? (
              <button type="button" className="profile-menu-item profile-menu-item--admin" onClick={() => { setOpen(false); navigate('/admin'); }}>
                <LayoutDashboard size={17} /> <span>Admin Console</span>
              </button>
            ) : !isAdmin ? (
              <button type="button" className="profile-menu-item" onClick={() => { setOpen(false); navigate('/dashboard'); }}>
                <User size={17} /> <span>Dashboard</span>
              </button>
            ) : null}
            <button type="button" className="profile-menu-item" onClick={() => { setOpen(false); navigate('/notifications'); }}>
              <Bell size={17} /> <span>Notifications</span>
            </button>
            <button type="button" className="profile-menu-item" onClick={() => { setOpen(false); navigate('/settings'); }}>
              <Settings size={17} /> <span>Settings</span>
            </button>
            <button type="button" className="profile-menu-item" onClick={() => { setOpen(false); logout(); navigate('/'); }}>
              <LogOut size={17} /> <span>Sign out</span>
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const AUTH_ROUTES = new Set(['/login', '/register']);

// #tag: DESKTOP APP HEADER NAVIGATION
// #purpose: Renders top navigation bar with brand logo, primary tabs, and account actions for wide screens.
function DesktopNavigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const { items, isLoading } = useNavItems();
  const activePath = useActiveNavPath(items);
  const { isSignedIn, isAdmin, usesAdminConsoleOnly } = useAuth();
  const onAuthScreen = AUTH_ROUTES.has(location.pathname);
  const { data: siteConfig } = useSiteConfig();
  const { data: notifData } = useNotifications();
  const unreadCount = notifData?.meta?.unread ?? 0;
  const platform = siteConfig?.platform;

  if (isLoading) return null;

  return (
    <header className="desktop-nav">
      <div className="nav-inner">
        <button type="button" className="brand-button" onClick={() => navigate('/')} aria-label="Open home">
          <img src={platform?.logoPath ?? DEFAULT_LOGO_PATH} alt="" className="brand-mark" />
          <span>{platform?.name}</span>
        </button>
        <nav className="desktop-nav-links" aria-label="Primary navigation">
          {items.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.path}
                type="button"
                className={`nav-pill ${activePath === item.path ? 'is-active' : ''}`}
                aria-current={activePath === item.path ? 'page' : undefined}
                onClick={() => navigate(item.path)}
              >
                <Icon size={17} aria-hidden="true" />
                <span>{item.label}</span>
              </button>
            );
          })}
          {isAdmin && !usesAdminConsoleOnly && (
            <button
              type="button"
              className="nav-pill nav-pill--admin"
              onClick={() => navigate('/admin')}
            >
              <LayoutDashboard size={17} aria-hidden="true" />
              <span>Admin</span>
            </button>
          )}
        </nav>
        <div className="nav-actions">
          {isSignedIn ? (
            <>
              <button
                type="button"
                className="ghost-icon-button bell-button"
                aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
                onClick={() => navigate('/notifications')}
              >
                <Bell size={19} aria-hidden="true" />
                {unreadCount > 0 && <span className="bell-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>}
              </button>
              <ProfileButton />
            </>
          ) : onAuthScreen ? null : (
            <button type="button" className="primary-action compact" onClick={() => navigate('/login')}>
              Sign in
            </button>
          )}
        </div>
      </div>
    </header>
  );
}

// #tag: MOBILE APP BAR
// #purpose: Renders compact header for small viewport mobile devices.
function MobileTopBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { items, isLoading } = useNavItems();
  const { data: siteConfig } = useSiteConfig();
  const platform = siteConfig?.platform;
  const current = items.find((item) =>
    item.path === '/' ? location.pathname === '/' : location.pathname.startsWith(item.path),
  );
  const title = AUTH_ROUTES.has(location.pathname)
    ? location.pathname === '/register'
      ? 'Create account'
      : 'Sign in'
    : !current || current.path === '/'
      ? platform?.name
      : current.label;
  const { isSignedIn } = useAuth();
  const onAuthScreen = AUTH_ROUTES.has(location.pathname);

  if (isLoading) return null;

  return (
    <header className="mobile-appbar">
      <div className="mobile-brand">
        <img src={platform?.logoPath ?? DEFAULT_LOGO_PATH} alt="" className="mobile-brand-mark" />
        <span>{title}</span>
      </div>
      {isSignedIn ? <ProfileButton compact /> : onAuthScreen ? null : (
        <button type="button" className="primary-action compact" onClick={() => navigate('/login')}>
          Sign in
        </button>
      )}
    </header>
  );
}

// #tag: MOBILE FLOATING BOTTOM DOCK
// #purpose: Renders touch-optimized navigation dock anchored at bottom of mobile viewports.
function BottomNavigation() {
  const navigate = useNavigate();
  const { items, isLoading } = useNavItems();
  const activePath = useActiveNavPath(items);
  if (isLoading || items.length === 0) return null;
  return <FloatingDock items={items} activePath={activePath} onNavigate={(path) => navigate(path)} />;
}

// #tag: MAIN CLIENT APP SHELL COMPONENT
// #purpose: Outer layout wrapper providing responsive navigation, main content router outlet, and transition animations.
export function AppShell() {
  const location = useLocation();
  const { isLoading } = useNavItems();

  return (
    <div className="app-shell min-h-screen">
      <a className="skip-link" href="#main">Skip to content</a>
      <DesktopNavigation />
      <MobileTopBar />
      <motion.main
        key={location.pathname}
        id="main"
        tabIndex={-1}
        className="app-main"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
      >
        {isLoading ? <LoadingState label="Loading…" /> : <Outlet />}
      </motion.main>
      <BottomNavigation />
    </div>
  );
}
